/**
 * ChangeControlRegister — the new GMP Change Control register (change_control).
 * KPI cards + status filter + table of CRs + a 5-step "New CR" wizard (CC-13:
 * Initiation → Classification → Impact → Attachments → Review, Save-as-Draft on
 * each step; classification auto-suggests from the CC-12 change-type rules).
 * Route: /change-control. Bilingual (en-US / ar-SA).
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@edms/design-system';
import styles from '../Recalls/Recalls.module.css';
import { apiFetch } from '../../api/client';
import {
  useChangeRequests, useCreateCr, useChangeTypes,
  CC_DEPARTMENTS, CC_EVIDENCE_CATEGORIES, CC_STATUS_LABEL, CC_LEVEL_LABEL, type CrSummary,
} from './useChangeControl';

const BLANK_FORM = {
  title: '', change_type: '', department: '', section: '', source_of_change: '', duration: 'PERMANENT',
  gmp_classification: 'GMP', change_level: 'LEVEL_2', description: '', current_practice: '', proposed_change: '', reason: '',
};
interface WizImpact { department: string; has_impact: string; assessment_text: string }
interface WizAttach { filename: string; mime: string; base64: string; category: string }
const TOTAL_STEPS = 5;

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function levelClass(l: string | null) {
  return l === 'LEVEL_3' ? styles.cls1 : l === 'LEVEL_2' ? styles.cls2 : styles.cls3;
}

export function ChangeControlRegister() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const q = useChangeRequests({ status: status || undefined });
  const items: CrSummary[] = q.data?.items ?? [];
  const stats = q.data?.stats;
  const tStatus = (s: string) => t(`changeControl.status.${s}`, { defaultValue: CC_STATUS_LABEL[s] ?? s });
  const tLevel = (l: string) => t(`changeControl.level.${l}`, { defaultValue: CC_LEVEL_LABEL[l] ?? l });
  const tDept = (d: string) => t(`changeControl.dept.${d}`, { defaultValue: d });

  const create = useCreateCr();
  const typesQ = useChangeTypes();
  const typeRules = typesQ.data?.items ?? [];

  // Wizard state.
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [impacts, setImpacts] = useState<WizImpact[]>([]);
  const [impForm, setImpForm] = useState({ department: 'PRODUCTION', has_impact: 'true', assessment_text: '' });
  const [attachments, setAttachments] = useState<WizAttach[]>([]);
  const [attachCat, setAttachCat] = useState('DOCUMENT');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function reset() {
    setStep(1); setForm({ ...BLANK_FORM }); setImpacts([]); setAttachments([]);
    setImpForm({ department: 'PRODUCTION', has_impact: 'true', assessment_text: '' }); setAttachCat('DOCUMENT'); setErr(null);
  }
  function openWizard() { reset(); setOpen(true); }

  // Classification: a change type applies its default GMP class + level (CC-12).
  function pickType(ct: string) {
    const rule = typeRules.find((r) => r.change_type === ct);
    setForm((f) => ({ ...f, change_type: ct, gmp_classification: rule?.default_gmp || f.gmp_classification, change_level: rule?.default_level || f.change_level }));
  }
  const activeRule = typeRules.find((r) => r.change_type === form.change_type);

  function addImpact() {
    if (!impForm.department) return;
    setImpacts((xs) => [...xs, { ...impForm }]);
    setImpForm({ ...impForm, assessment_text: '' });
  }
  function addSuggestedDepts() {
    if (!activeRule) return;
    setImpacts((xs) => {
      const have = new Set(xs.map((x) => x.department));
      const add = activeRule.required_departments.filter((d) => !have.has(d)).map((d) => ({ department: d, has_impact: 'true', assessment_text: '' }));
      return [...xs, ...add];
    });
  }
  function addAttachmentFile(file: File | null) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || '');
      const base64 = res.includes(',') ? res.slice(res.indexOf(',') + 1) : res;
      setAttachments((a) => [...a, { filename: file.name, mime: file.type || 'application/octet-stream', base64, category: attachCat }]);
    };
    reader.readAsDataURL(file);
  }

  async function persist(submit: boolean) {
    setErr(null);
    if (!form.title.trim()) { setErr(t('changeControl.form.titleRequired')); setStep(1); return; }
    setBusy(true);
    try {
      const soc = form.source_of_change.split(',').map((sx) => sx.trim()).filter(Boolean);
      const created = await create.mutateAsync({
        title: form.title.trim(),
        change_type: form.change_type || undefined,
        department: form.department.trim() || undefined,
        section: form.section.trim() || undefined,
        source_of_change: soc.length ? soc : undefined,
        duration: form.duration,
        gmp_classification: form.gmp_classification,
        change_level: form.change_level,
        description: form.description.trim() || undefined,
        current_practice: form.current_practice.trim() || undefined,
        proposed_change: form.proposed_change.trim() || undefined,
        reason: form.reason.trim() || undefined,
      });
      const id = created.cr_id;
      for (const im of impacts) {
        await apiFetch(`/api/v1/change-control/requests/${id}/impacts`, { method: 'POST', body: { department: im.department, has_impact: im.has_impact === 'true', assessment_text: im.assessment_text.trim() || undefined } });
      }
      for (const at of attachments) {
        await apiFetch(`/api/v1/change-control/requests/${id}/evidence`, { method: 'POST', body: { category: at.category, title: at.filename, filename: at.filename, mime: at.mime, artifactBase64: at.base64 } });
      }
      if (submit) await apiFetch(`/api/v1/change-control/requests/${id}/transition`, { method: 'POST', body: { to_status: 'SUBMITTED' } });
      setOpen(false); reset();
      navigate({ to: '/change-control/$id', params: { id } });
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : t('changeControl.form.createError')); }
    finally { setBusy(false); }
  }

  const stepName = (n: number) => t(`changeControl.wizard.s${n}`);

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow={t('changeControl.register.eyebrow')}
        title={t('changeControl.register.title')}
        subtitle={t('changeControl.register.subtitle')}
      />

      {stats && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.total}</span><span className={styles.kpiLabel}>{t('changeControl.register.kpiTotal')}</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.open}</span><span className={styles.kpiLabel}>{t('changeControl.register.kpiOpen')}</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.closed}</span><span className={styles.kpiLabel}>{t('changeControl.register.kpiClosed')}</span></div>
          <div className={`${styles.kpi} ${Number(stats.critical) > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.critical}</span><span className={styles.kpiLabel}>{t('changeControl.register.kpiCritical')}</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.pending_approval}</span><span className={styles.kpiLabel}>{t('changeControl.register.kpiPending')}</span></div>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label={t('changeControl.register.statusFilterAria')} value={status} onChange={(e) => setStatus(e.target.value)} className={styles.select}>
          <option value="">{t('changeControl.register.allStatuses')}</option>
          {Object.keys(CC_STATUS_LABEL).map((s) => <option key={s} value={s}>{tStatus(s)}</option>)}
        </select>
        <span className={styles.resultCount}>{t('changeControl.register.count', { count: items.length })}</span>
        <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} style={{ marginInlineStart: 'auto' }} onClick={() => navigate({ to: '/change-control/dashboard' })}>
          📊 {t('changeControl.dashboard.open')}
        </button>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={openWizard}>
          + {t('changeControl.register.newCr')}
        </button>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>{t('changeControl.register.loading')}</div>
      ) : q.isError ? (
        <div className={styles.empty}>{t('changeControl.register.loadError')}</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>{t('changeControl.register.empty')}</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('changeControl.register.thCr')}</th><th>{t('changeControl.register.thTitle')}</th><th>{t('changeControl.register.thGmp')}</th>
              <th>{t('changeControl.register.thLevel')}</th><th>{t('changeControl.register.thStatus')}</th><th>{t('changeControl.register.thInitiator')}</th><th>{t('changeControl.register.thRaised')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.cr_id} className={styles.row} onClick={() => navigate({ to: '/change-control/$id', params: { id: r.cr_id } })}>
                <td className={styles.mono}>
                  {r.cr_number ?? '—'}
                  {r.migration_label === 'IMPORTED_DATA_GAP'
                    ? <span className={styles.gapTag} title={t('changeControl.register.dataGapTitle', { defaultValue: 'On disk but missing from the official CR Log — QA to reconcile.' })}>{t('changeControl.register.dataGap', { defaultValue: 'Not in CR Log' })}</span>
                    : r.migration_label && <span className={styles.mockTag} title={t('changeControl.register.importedTitle', { label: r.migration_label })}>{t('changeControl.register.imported')}</span>}
                </td>
                <td>{r.title}</td>
                <td>{r.gmp_classification === 'GMP' ? <span className={`${styles.pill} ${styles.cls2}`}>{t('changeControl.register.gmpYes')}</span> : r.gmp_classification === 'NON_GMP' ? <span className={`${styles.pill} ${styles.cls3}`}>{t('changeControl.register.gmpNo')}</span> : '—'}</td>
                <td>{r.change_level ? <span className={`${styles.pill} ${levelClass(r.change_level)}`}>{tLevel(r.change_level)}</span> : '—'}</td>
                <td><span className={styles.statusPill}>{tStatus(r.status)}</span></td>
                <td>{r.initiator_name ?? '—'}</td>
                <td>{fmtDate(r.created_at_utc)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal aria-label={t('changeControl.form.title')}>
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>{t('changeControl.form.title')}</h2>
            {/* Step strip */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setStep(n)}
                  style={{
                    flex: 1, minWidth: 90, padding: '6px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: '1px solid var(--color-border-default, #DCE3EB)',
                    background: n === step ? 'var(--color-brand-600, #1570EF)' : n < step ? '#ECFDF3' : '#fff',
                    color: n === step ? '#fff' : n < step ? '#067647' : 'var(--color-text-tertiary, #6B7989)',
                  }}>
                  {n < step ? '✓ ' : `${n}. `}{stepName(n)}
                </button>
              ))}
            </div>
            <p className={styles.resultCount}>{t('changeControl.wizard.stepOf', { n: step, total: TOTAL_STEPS })}</p>
            {err && <div className={styles.err} role="alert">{err}</div>}

            {/* Step 1 — Initiation */}
            {step === 1 && (<>
              <label className={styles.field}><span>{t('changeControl.form.titleField')} *</span>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('changeControl.form.titlePh')} /></label>
              <div className={styles.field2}>
                <label className={styles.field}><span>{t('changeControl.form.department')}</span>
                  <input type="text" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder={t('changeControl.form.departmentPh')} /></label>
                <label className={styles.field}><span>{t('changeControl.form.section')}</span>
                  <input type="text" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} /></label>
              </div>
              <label className={styles.field}><span>{t('changeControl.form.description')}</span>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
              <label className={styles.field}><span>{t('changeControl.form.currentPractice')}</span>
                <textarea rows={2} value={form.current_practice} onChange={(e) => setForm({ ...form, current_practice: e.target.value })} /></label>
              <label className={styles.field}><span>{t('changeControl.form.proposedChange')}</span>
                <textarea rows={2} value={form.proposed_change} onChange={(e) => setForm({ ...form, proposed_change: e.target.value })} /></label>
              <label className={styles.field}><span>{t('changeControl.form.reason')}</span>
                <textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></label>
            </>)}

            {/* Step 2 — Classification */}
            {step === 2 && (<>
              <label className={styles.field}><span>{t('changeControl.form.changeType')}</span>
                <select value={form.change_type} onChange={(e) => pickType(e.target.value)}>
                  <option value="">{t('changeControl.form.changeTypePh')}</option>
                  {typeRules.map((r) => <option key={r.change_type} value={r.change_type}>{t(`changeControl.changeType.${r.change_type}`, { defaultValue: r.label })}</option>)}
                </select></label>
              {activeRule && (
                <p className={styles.resultCount}>{t('changeControl.form.typeHint', { reqs: [
                  ...activeRule.required_departments.map((d) => tDept(d)),
                  ...activeRule.required_assessments,
                ].join(' · ') })}</p>
              )}
              <div className={styles.field2}>
                <label className={styles.field}><span>{t('changeControl.form.gmpClass')}</span>
                  <select value={form.gmp_classification} onChange={(e) => setForm({ ...form, gmp_classification: e.target.value })}>
                    <option value="GMP">{t('changeControl.register.gmpYes')}</option>
                    <option value="NON_GMP">{t('changeControl.register.gmpNo')}</option>
                  </select></label>
                <label className={styles.field}><span>{t('changeControl.form.level')}</span>
                  <select value={form.change_level} onChange={(e) => setForm({ ...form, change_level: e.target.value })}>
                    <option value="LEVEL_1">{t('changeControl.form.level1')}</option>
                    <option value="LEVEL_2">{t('changeControl.form.level2')}</option>
                    <option value="LEVEL_3">{t('changeControl.form.level3')}</option>
                  </select></label>
              </div>
              <div className={styles.field2}>
                <label className={styles.field}><span>{t('changeControl.form.duration')}</span>
                  <select value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}>
                    <option value="PERMANENT">{t('changeControl.form.permanent')}</option>
                    <option value="TEMPORARY">{t('changeControl.form.temporary')}</option>
                  </select></label>
                <label className={styles.field}><span>{t('changeControl.form.source')}</span>
                  <input type="text" value={form.source_of_change} onChange={(e) => setForm({ ...form, source_of_change: e.target.value })} placeholder={t('changeControl.form.sourcePh')} /></label>
              </div>
            </>)}

            {/* Step 3 — Impact */}
            {step === 3 && (<>
              {impacts.length > 0 ? (
                <table className={styles.table}>
                  <thead><tr><th>{t('changeControl.register.thInitiator') /* Department */}</th><th>{t('changeControl.wizard.thHasImpact')}</th><th>{t('changeControl.wizard.thAssessment')}</th><th aria-label="x" /></tr></thead>
                  <tbody>
                    {impacts.map((im, k) => (
                      <tr key={k}><td>{tDept(im.department)}</td><td>{im.has_impact === 'true' ? t('changeControl.wizard.yes') : t('changeControl.wizard.no')}</td><td>{im.assessment_text || '—'}</td>
                        <td><button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setImpacts((xs) => xs.filter((_, i) => i !== k))}>✕</button></td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className={styles.resultCount}>{t('changeControl.wizard.impactNone')}</p>}
              {activeRule && activeRule.required_departments.length > 0 && (
                <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} style={{ marginBottom: 8 }} onClick={addSuggestedDepts}>+ {t('changeControl.wizard.addSuggested')}</button>
              )}
              <div className={styles.field2}>
                <label className={styles.field}><span>{t('changeControl.form.department')}</span>
                  <select value={impForm.department} onChange={(e) => setImpForm({ ...impForm, department: e.target.value })}>
                    {CC_DEPARTMENTS.map((d) => <option key={d} value={d}>{tDept(d)}</option>)}
                  </select></label>
                <label className={styles.field}><span>{t('changeControl.wizard.thHasImpact')}</span>
                  <select value={impForm.has_impact} onChange={(e) => setImpForm({ ...impForm, has_impact: e.target.value })}>
                    <option value="true">{t('changeControl.wizard.yes')}</option>
                    <option value="false">{t('changeControl.wizard.no')}</option>
                  </select></label>
              </div>
              <label className={styles.field}><span>{t('changeControl.wizard.thAssessment')}</span>
                <input type="text" value={impForm.assessment_text} onChange={(e) => setImpForm({ ...impForm, assessment_text: e.target.value })} /></label>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={addImpact}>+ {t('changeControl.wizard.impactAdd')}</button>
            </>)}

            {/* Step 4 — Attachments */}
            {step === 4 && (<>
              {attachments.length > 0 ? (
                <ul style={{ margin: '0 0 10px', paddingInlineStart: 18 }}>
                  {attachments.map((at, k) => (
                    <li key={k} style={{ fontSize: 13, marginBottom: 4 }}>
                      <span className={styles.mono}>{at.filename}</span> — {t(`changeControl.evidenceCategory.${at.category}`, { defaultValue: at.category })}
                      <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} style={{ marginInlineStart: 8, padding: '0 8px' }} onClick={() => setAttachments((a) => a.filter((_, i) => i !== k))}>✕</button>
                    </li>
                  ))}
                </ul>
              ) : <p className={styles.resultCount}>{t('changeControl.wizard.attachNone')}</p>}
              <div className={styles.field2}>
                <label className={styles.field}><span>{t('changeControl.evidence.category')}</span>
                  <select value={attachCat} onChange={(e) => setAttachCat(e.target.value)}>
                    {CC_EVIDENCE_CATEGORIES.map((c) => <option key={c} value={c}>{t(`changeControl.evidenceCategory.${c}`, { defaultValue: c })}</option>)}
                  </select></label>
                <label className={styles.field}><span>{t('changeControl.wizard.attachFile')}</span>
                  <input type="file" onChange={(e) => { addAttachmentFile(e.target.files?.[0] ?? null); e.target.value = ''; }} /></label>
              </div>
            </>)}

            {/* Step 5 — Review */}
            {step === 5 && (<>
              <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>{t('changeControl.wizard.reviewSummary')}</h3>
              <dl className={styles.metaGrid}>
                <div><dt>{t('changeControl.form.titleField')}</dt><dd>{form.title || '—'}</dd></div>
                <div><dt>{t('changeControl.wizard.reviewType')}</dt><dd>{form.change_type ? t(`changeControl.changeType.${form.change_type}`, { defaultValue: form.change_type }) : '—'} · {form.gmp_classification} · {tLevel(form.change_level)}</dd></div>
                <div><dt>{t('changeControl.form.department')}</dt><dd>{form.department || '—'}</dd></div>
                <div><dt>{t('changeControl.wizard.s3')}</dt><dd>{t('changeControl.wizard.reviewImpacts', { n: impacts.length })}</dd></div>
                <div><dt>{t('changeControl.wizard.s4')}</dt><dd>{t('changeControl.wizard.reviewAttachments', { n: attachments.length })}</dd></div>
              </dl>
              <p className={styles.resultCount}>{t('changeControl.wizard.draftHint')}</p>
            </>)}

            {/* Footer */}
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setOpen(false)}>{t('changeControl.form.cancel')}</button>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} disabled={busy} onClick={() => void persist(false)}>{t('changeControl.wizard.saveDraft')}</button>
              {step > 1 && <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setStep((s) => s - 1)}>← {t('changeControl.wizard.back')}</button>}
              {step < TOTAL_STEPS
                ? <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setStep((s) => s + 1)}>{t('changeControl.wizard.next')} →</button>
                : <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={() => void persist(true)}>{busy ? t('changeControl.wizard.submitting') : t('changeControl.wizard.submit')}</button>}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
